<script lang="ts" setup>
import GifIcon from '@/components/common/icons/GifIcon.vue'
import { emitter } from '@/main'
import { Capacitor } from '@capacitor/core'
import {
  Attachment01Icon,
  Camera01Icon,
  FaceSmileIcon,
  Film02Icon,
  Hash02Icon,
  Image01Icon,
  Link01Icon,
  PlusCircleIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { renderIcon, UITooltip } from '@gohighlevel/ghl-ui'
import { Editor } from '@tiptap/vue-3'
import { NDropdown } from 'naive-ui'
import { computed, h, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EmbedsMobile from './EmbedsMobile/EmbedsMobile.vue'
const props = defineProps({
  editor: Editor,
  selectedEmbed: String,
  editorType: String,
  widthInPx: Number,
  isWideScreen: Boolean,
  autoFocus: Boolean,
  isCommentDisabled: {
    type: Boolean,
    default: false,
  },
  closeHiddenOptions: {
    type: Boolean,
    default: false,
  },
})

const disableAttachments = ref(false)
const showMoreOptionsPopover = ref(false)
const dropdownTrigger = ref<HTMLElement | null>(null)
const { t } = useI18n()

watch(
  () => props.closeHiddenOptions,
  value => {
    if (value) {
      showMoreOptionsPopover.value = false
    }
  }
)
onBeforeMount(() => {
  if (
    props.editorType === 'PostEditor' ||
    props.editorType === 'CommentEditor'
  ) {
    embedIcons.splice(3, 0, {
      id: 'link',
      title: t('embeds.addLink'),
      limitToOne: false,
      component: Link01Icon,
      suggestion: '',
      showOnMobile: true,
      reaction: () => toggleShowModal('link'),
    })
  }
  if (props.editorType === 'ChatEditor') {
    // find and remove emoji icon from embedIcons
    const emojiIndex = embedIcons.findIndex(icon => icon.id === 'emoji')
    embedIcons.splice(emojiIndex, 1)

    // find and remove image from embedIcons
    const imageIndex = embedIcons.findIndex(icon => icon.id === 'image')
    embedIcons.splice(imageIndex, 1)

    // find and update video from embedIcons
    const videoIndex = embedIcons.findIndex(icon => icon.id === 'video')
    embedIcons.at(videoIndex)!.title = t('chat.media')
    embedIcons.at(videoIndex)!.id = 'video'

    // find and remove hashtag from embedIcons
    const hashtagIndex = embedIcons.findIndex(icon => icon.id === 'hashtag')
    embedIcons.splice(hashtagIndex, 1)

    // find and update attachments from embedIcons
    const attachmentIndex = embedIcons.findIndex(
      icon => icon.id === 'attachment'
    )
    embedIcons.at(attachmentIndex)!.title = t('chat.files')
    embedIcons.at(attachmentIndex)!.id = 'attachment'
  }
})

function handleAttachmentsUpdate(_value) {
  if (props.editorType !== 'CommentEditor') {
    return
  }
  // disableAttachments.value = value === 0 || !value ? false : true
}

const emit = defineEmits([
  'add-attachment',
  'update:selectedEmbed',
  'update:isCommentDisabled',
])

emitter.on('attachments-update', handleAttachmentsUpdate)

function toggleShowModal(type: string) {
  emit('update:selectedEmbed', type)
}

const embedIcons = [
  {
    id: 'gif',
    title: t('embeds.addGif'),
    component: GifIcon,
    limitToOne: false,
    suggestion: t('embeds.gifSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('gif'),
  },
  {
    id: 'image',
    title: t('embeds.addImage'),
    component: Capacitor.isNativePlatform() ? Camera01Icon : Image01Icon,
    suggestion: t('embeds.imageSuggestion'),
    limitToOne: true,
    showOnMobile: true,
    reaction: () => toggleShowModal('image'),
  },
  {
    id: 'video',
    title: t('embeds.addVideo'),
    component: Capacitor.isNativePlatform() ? Image01Icon : Film02Icon,
    limitToOne: true,
    suggestion: t('embeds.videoSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('video'),
  },
  {
    id: 'attachment',
    title: t('embeds.addAttachment'),
    limitToOne: true,
    component: Attachment01Icon,
    suggestion: t('embeds.attachmentSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('attachment'),
  },
  {
    id: 'emoji',
    title: t('embeds.addEmoji'),
    component: FaceSmileIcon,
    limitToOne: false,
    suggestion: t('embeds.emojiSuggestion'),
    showOnMobile: false,
    reaction: () => toggleShowModal('emoji'),
  },
  {
    id: 'hashtag',
    title: t('embeds.addHashtag'),
    component: Hash02Icon,
    limitToOne: false,
    suggestion: t('embeds.hashtagSuggestion'),
    showOnMobile: true,
    reaction: () => toggleShowModal('hashtag'),
  },
]

const visibleIcons = computed(() => {
  if (props.widthInPx !== undefined && props.widthInPx !== null) {
    const maxVisible = Math.floor(props.widthInPx / 40) // Assuming each icon needs 40px
    return embedIcons.slice(0, maxVisible - 1)
  } else {
    return embedIcons
  }
})
const hiddenIcons = computed(() => {
  if (props.widthInPx !== undefined && props.widthInPx !== null) {
    const maxVisible = Math.floor(props.widthInPx / 40) // Assuming each icon needs 100px
    return embedIcons.slice(maxVisible - 1).map(icon => {
      return {
        label: icon.title,
        key: icon.id,
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(icon.component), null, {})
        },
      }
    })
  } else {
    return []
  }
})

function handleClickOutside(event: Event) {
  // Check if the click target is not the trigger
  if (
    dropdownTrigger.value &&
    !dropdownTrigger.value.contains(event.target as Node)
  ) {
    showMoreOptionsPopover.value = false
  }
}
</script>
<template>
  <div
    v-if="isWideScreen && editorType !== 'ChatEditor'"
    class="flex text-communities-font-secondary sm:items-center sm:justify-center"
  >
    <div v-for="icons in embedIcons" id="media-embed-button" :key="icons.id">
      <div
        v-if="
          icons.limitToOne === false ||
          (icons.limitToOne === true && !disableAttachments)
        "
      >
        <UITooltip placement="top">
          <span>{{ icons.title }}</span>
          <template #trigger>
            <div
              class="mx-1 flex h-10 w-10 items-center justify-center rounded-full bg-communities-fill"
              :class="
                selectedEmbed === icons.id
                  ? 'bg-communities-background text-communities-font-primary'
                  : 'bg-communities-background text-communities-font-secondary'
              "
            >
              <div
                class="justfiy-center flex h-8 w-8 cursor-pointer items-center rounded-full bg-communities-fill hover:bg-communities-border"
                :class="
                  selectedEmbed === icons.id
                    ? 'text-communities-primary'
                    : 'bg-communities-background text-communities-font-secondary'
                "
                :data-embed-type="icons.id"
                @click="icons.reaction"
              >
                <component :is="icons.component" class="h-4 w-4 px-2" />
              </div>
            </div>
          </template>
        </UITooltip>
      </div>
    </div>
  </div>
  <div
    v-else-if="isWideScreen && editorType === 'ChatEditor'"
    class="flex items-center justify-center gap-3 text-communities-font-secondary"
  >
    <div v-if="hiddenIcons.length > 0">
      <!-- UIPopover for showing hidden options -->
      <NDropdown
        id="chat-action-dropdown"
        :options="hiddenIcons"
        placement="top-start"
        class="rounded-3xl"
        trigger="manual"
        :show="showMoreOptionsPopover"
        @select="toggleShowModal"
        @clickoutside="handleClickOutside"
      >
        <div
          id="chat-action-dropdown-trigger"
          ref="dropdownTrigger"
          class="flex h-10 w-10 items-center justify-center rounded-lg border border-solid"
          :class="
            showMoreOptionsPopover
              ? 'border-communities-font-primary text-communities-font-primary'
              : 'border-communities-border text-communities-font-secondary'
          "
          tabindex="0"
          role="button"
          aria-label="Open Embeds"
          @click.stop="showMoreOptionsPopover = !showMoreOptionsPopover"
          @keyup.enter="showMoreOptionsPopover = !showMoreOptionsPopover"
        >
          <PlusCircleIcon
            id="chat-action-dropdown-trigger-icon"
            class="h-5 w-5"
          />
        </div>
      </NDropdown>
    </div>
    <div v-for="icon in visibleIcons" id="media-embed-button-" :key="icon.id">
      <div
        :id="icon.id"
        class="flex h-10 w-10 items-center justify-center rounded-lg border border-solid"
        :class="
          selectedEmbed === icon.id
            ? 'border-communities-font-primary text-communities-font-primary'
            : 'border-communities-border text-communities-font-secondary'
        "
        @click="icon.reaction"
      >
        <component :is="icon.component" class="h-5 w-5" />
      </div>
    </div>
  </div>
  <EmbedsMobile
    v-else-if="!isWideScreen && !disableAttachments"
    :editor="props.editor"
    :selected-embed="props.selectedEmbed"
    :is-wide-screen="props.isWideScreen"
    :editor-type="props.editorType"
    :auto-focus="autoFocus"
    :is-comment-disabled="props.isCommentDisabled"
    @add-attachment="emit('add-attachment')"
    @update:selectedEmbed="emit('update:selectedEmbed', $event)"
    @update:isCommentDisabled="emit('update:isCommentDisabled', $event)"
  />
</template>
<style scoped>
#media-embed-button {
  box-sizing: revert !important;
}
.embed-item:hover {
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06),
    0px -1px 8px -2px rgba(16, 24, 40, 0.1) !important;
}
.n-divider {
  margin: 0px !important;
}
</style>
<style>
.n-popover:not(.n-popover--raw):not(.n-popover--scrollable):not(
    .n-popover--show-header-or-footer
  ) {
  padding: 0px !important;
}
</style>
